import { useEffect } from 'react';
import { useUserStore } from '../store/userStore';
import UserService from '../service/UserService';
import type { CreateUserInput, UpdateUserInput, CognitoUser } from '../types/cognitoUser';

/**
 * Custom hook for Cognito user management operations.
 * Loads users on mount and provides CRUD functions.
 */
export const useUsers = () => {
  const store = useUserStore();
  const userService = new UserService();

  const loadUsers = async () => {
    store.setLoading(true);
    store.setError(null);

    try {
      let allUsers: CognitoUser[] = [];
      let nextToken: string | undefined;

      do {
        const result = await userService.listUsers({ limit: 60, nextToken });
        const page = result.data?.listUsers;
        allUsers = allUsers.concat(page?.users ?? []);
        nextToken = page?.nextToken ?? undefined;
      } while (nextToken);

      store.setUsers(allUsers);
    } catch (error) {
      console.error('Failed to load users:', error);
      store.setError(error instanceof Error ? error.message : 'Failed to load users');
    } finally {
      store.setLoading(false);
    }
  };

  const loadUser = async (username: string): Promise<CognitoUser | null> => {
    try {
      const result = await userService.getUser(username);
      const user = result.data?.getUser ?? null;
      if (user) {
        store.setSelectedUser(user);
      }
      return user;
    } catch (error) {
      console.error('Failed to load user:', error);
      store.setError(error instanceof Error ? error.message : 'Failed to load user');
      return null;
    }
  };

  const createUser = async (input: CreateUserInput): Promise<CognitoUser | null> => {
    store.setLoading(true);
    store.setError(null);

    try {
      const result = await userService.createUser(input);
      if (result.errors?.length) {
        throw new Error(result.errors[0].message);
      }
      await loadUsers();
      return result.data?.createUser ?? null;
    } catch (error) {
      console.error('Failed to create user:', error);
      const message = error instanceof Error ? error.message : 'Failed to create user';
      store.setError(message);
      throw error;
    } finally {
      store.setLoading(false);
    }
  };

  const updateUser = async (input: UpdateUserInput): Promise<CognitoUser | null> => {
    store.setLoading(true);
    store.setError(null);

    try {
      const result = await userService.updateUser(input);
      if (result.errors?.length) {
        throw new Error(result.errors[0].message);
      }
      await loadUsers();
      return result.data?.updateUser ?? null;
    } catch (error) {
      console.error('Failed to update user:', error);
      const message = error instanceof Error ? error.message : 'Failed to update user';
      store.setError(message);
      throw error;
    } finally {
      store.setLoading(false);
    }
  };

  const deleteUser = async (username: string): Promise<boolean> => {
    store.setLoading(true);
    store.setError(null);

    try {
      const result = await userService.deleteUser(username);
      if (result.errors?.length) {
        throw new Error(result.errors[0].message);
      }
      store.setSelectedUser(null);
      await loadUsers();
      return true;
    } catch (error) {
      console.error('Failed to delete user:', error);
      const message = error instanceof Error ? error.message : 'Failed to delete user';
      store.setError(message);
      throw error;
    } finally {
      store.setLoading(false);
    }
  };

  const disableUser = async (username: string): Promise<CognitoUser | null> => {
    store.setLoading(true);
    store.setError(null);

    try {
      const result = await userService.disableUser(username);
      await loadUsers();
      return result.data?.disableUser ?? null;
    } catch (error) {
      console.error('Failed to disable user:', error);
      const message = error instanceof Error ? error.message : 'Failed to disable user';
      store.setError(message);
      throw error;
    } finally {
      store.setLoading(false);
    }
  };

  const enableUser = async (username: string): Promise<CognitoUser | null> => {
    store.setLoading(true);
    store.setError(null);

    try {
      const result = await userService.enableUser(username);
      await loadUsers();
      return result.data?.enableUser ?? null;
    } catch (error) {
      console.error('Failed to enable user:', error);
      const message = error instanceof Error ? error.message : 'Failed to enable user';
      store.setError(message);
      throw error;
    } finally {
      store.setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    ...store,
    loadUsers,
    loadUser,
    createUser,
    updateUser,
    deleteUser,
    disableUser,
    enableUser,
  };
};
